import { useEffect, useRef, useState } from "react";
import { Play, Square, RotateCcw } from "lucide-react";
import { Console } from "@/components/Console";
import { PreviewLoader } from "@/components/PreviewLoader";

const PG_KEY = "__pg_motion__";

const buildSrcDoc = (html, css, js) => `<!DOCTYPE html>
<html>
<head>
<meta charset="utf-8" />
<meta name="viewport" content="width=device-width, initial-scale=1" />
<style>
html,body{margin:0;padding:0;height:100%;}
*{box-sizing:border-box;}
/* sandbox scrollbar — matches the IDE aesthetic */
*{scrollbar-width:thin;scrollbar-color:rgba(99,102,241,.55) transparent;}
*::-webkit-scrollbar{width:10px;height:10px;}
*::-webkit-scrollbar-track{background:transparent;}
*::-webkit-scrollbar-thumb{background:rgba(120,130,160,.45);border-radius:8px;border:2px solid transparent;background-clip:content-box;transition:background .2s;}
*::-webkit-scrollbar-thumb:hover{background:rgba(99,102,241,.75);background-clip:content-box;}
*::-webkit-scrollbar-corner{background:transparent;}
${css}
</style>
</head>
<body>
${html}
<script>
(function(){
  var send=function(level,args){
    try{
      parent.postMessage({${JSON.stringify(PG_KEY)}:true,level:level,msg:Array.prototype.slice.call(args).map(function(a){
        try{return (typeof a==='object'&&a!==null)?JSON.stringify(a):String(a);}catch(e){return String(a);}
      }).join(' ')},'*');
    }catch(e){}
  };
  var signal=function(type){try{parent.postMessage({${JSON.stringify(PG_KEY)}:true,type:type},'*');}catch(e){}};
  ['log','info','warn','error','debug'].forEach(function(l){
    var orig=console[l]?console[l].bind(console):function(){};
    console[l]=function(){send(l==='debug'?'log':l,arguments);orig.apply(console,arguments);};
  });
  window.addEventListener('error',function(e){send('error',[e.message+'  ('+e.lineno+':'+e.colno+')']);});
  window.addEventListener('unhandledrejection',function(e){send('error',['Unhandled Promise: '+((e.reason&&e.reason.message)||e.reason)]);});
  window.__pgReady=signal;
})();
window.addEventListener('load',function(){
  try{
${js}
  }catch(err){console.error(err.message);}
  try{ window.__pgReady('__ready__'); }catch(e){}
});
</script>
</body>
</html>`;

export const Preview = ({ code }) => {
  const iframeRef = useRef(null);
  const fallbackRef = useRef(null);
  const hideRef = useRef(null);
  const swapRef = useRef(null);
  const glowRef = useRef(null);
  const startRef = useRef(0);
  const codeRef = useRef(code);
  codeRef.current = code;
  const [running, setRunning] = useState(false);
  const [rendering, setRendering] = useState(true);
  const [logs, setLogs] = useState([]);
  const [glow, setGlow] = useState(false);

  // Minimum time the loader stays visible so it never "flashes" on fast renders.
  const MIN_LOADER_MS = 480;

  const hideLoader = () => {
    clearTimeout(fallbackRef.current);
    clearTimeout(hideRef.current);
    const elapsed = Date.now() - startRef.current;
    const remain = Math.max(0, MIN_LOADER_MS - elapsed);
    hideRef.current = setTimeout(() => setRendering(false), remain);
  };

  // Perform the actual srcdoc swap (always uses the latest code via ref).
  const swap = () => {
    setLogs([]);
    setRunning(true);
    startRef.current = Date.now();
    const c = codeRef.current;
    const doc = buildSrcDoc(c.html || "", c.css || "", c.js || "");
    if (iframeRef.current) iframeRef.current.srcdoc = doc;
    clearTimeout(fallbackRef.current);
    // Fallback: ensure the loader never gets stuck (e.g. blocking script).
    fallbackRef.current = setTimeout(() => setRendering(false), 5000);
  };

  const flashGlow = () => {
    clearTimeout(glowRef.current);
    setGlow(true);
    glowRef.current = setTimeout(() => setGlow(false), 600);
  };

  const run = () => {
    clearTimeout(swapRef.current);
    clearTimeout(hideRef.current);
    setRendering(true);
    flashGlow();
    swap();
  };

  useEffect(() => {
    const handler = (e) => {
      const d = e.data;
      if (!d || !d[PG_KEY]) return;
      if (d.type === "__ready__") {
        hideLoader();
        return;
      }
      setLogs((prev) => [
        ...prev.slice(-199),
        { level: d.level, msg: d.msg, t: Date.now() },
      ]);
    };
    window.addEventListener("message", handler);
    return () => window.removeEventListener("message", handler);
  }, []);

  const stop = () => {
    setRunning(false);
    setRendering(false);
    clearTimeout(fallbackRef.current);
    clearTimeout(hideRef.current);
    clearTimeout(swapRef.current);
    if (iframeRef.current) iframeRef.current.srcdoc = "";
    setLogs((prev) => [...prev, { level: "info", msg: "▣ 已停止运行", t: Date.now() }]);
  };

  const retry = () => run();

  // Auto-run (debounced) whenever code/course changes.
  // The loader is shown *immediately* so stale content is never visible
  // during the debounce window — this removes the "content → loader → content" flicker.
  useEffect(() => {
    setRendering(true);
    flashGlow();
    clearTimeout(hideRef.current);
    clearTimeout(fallbackRef.current);
    clearTimeout(swapRef.current);
    swapRef.current = setTimeout(() => swap(), 400);
    return () => clearTimeout(swapRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [code]);

  useEffect(
    () => () => {
      clearTimeout(fallbackRef.current);
      clearTimeout(hideRef.current);
      clearTimeout(swapRef.current);
      clearTimeout(glowRef.current);
    },
    []
  );

  return (
    <div className="h-full flex flex-col bg-[hsl(var(--surface))] min-w-0">
      <div className="flex items-center gap-1 px-3 h-11 border-b border-border shrink-0">
        <span className="text-[10px] font-mono uppercase tracking-[0.2em] text-muted-foreground mr-2">
          预览
        </span>
        <button
          data-testid="run-btn"
          onClick={run}
          title="运行 (Run)"
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-mono text-muted-foreground hover:text-emerald-500 hover:bg-muted transition-colors"
        >
          <Play className="h-3.5 w-3.5" /> 运行
        </button>
        <button
          data-testid="retry-btn"
          onClick={retry}
          title="重试 (Retry)"
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-mono text-muted-foreground hover:text-blue-500 hover:bg-muted transition-colors"
        >
          <RotateCcw className="h-3.5 w-3.5" /> 重试
        </button>
        <button
          data-testid="stop-btn"
          onClick={stop}
          title="停止 (Stop)"
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-mono text-muted-foreground hover:text-rose-500 hover:bg-muted transition-colors"
        >
          <Square className="h-3.5 w-3.5" /> 停止
        </button>
        <span
          data-testid="preview-status"
          className={`ml-auto flex items-center gap-1.5 text-[10px] font-mono ${
            rendering ? "text-sky-400" : running ? "text-emerald-500" : "text-muted-foreground"
          }`}
        >
          <span
            className={`h-1.5 w-1.5 rounded-full ${
              rendering
                ? "bg-sky-400 animate-pulse"
                : running
                ? "bg-emerald-500 animate-pulse"
                : "bg-muted-foreground"
            }`}
          />
          {rendering ? "RENDERING" : running ? "RUNNING" : "IDLE"}
        </span>
      </div>

      <div className="flex-1 min-h-0 bg-[#060818] relative">
        <iframe
          ref={iframeRef}
          data-testid="preview-iframe"
          title="playground-preview"
          sandbox="allow-scripts allow-same-origin allow-modals allow-popups allow-forms"
          className={`w-full h-full border-0 bg-white ${glow ? "run-glow" : ""}`}
        />
        <PreviewLoader visible={rendering} />
      </div>

      <Console logs={logs} onClear={() => setLogs([])} />
    </div>
  );
};
